/**
 * `dropExtension` — file drag-and-drop routing: `.md` → bulk import, other → attachment
 * (editor-chat.md §1.2, §2.1, §3.1, §5 step 6).
 *
 * editor-chat.md §1.2: paste and drag-drop normalize *into* markdown at the boundary. Dropped
 * `.md` files are **ingested as new notes** (handed to the backend import path — NOT pasted
 * into the current note); dropped non-`.md` files become **attachments** of the current note.
 *
 * editor-chat.md §3.1 / §6 OQ-5 (REVIEW M1): the attachments contract — the table, the upload
 * endpoint, the note↔attachment reference — is a separate workstream's responsibility and may
 * not be wired yet. So the attachment path here is a **routing decision that surfaces a
 * `DropOutcome`**, not a silent drop: a non-`.md` drop produces `attachment` (when an upload
 * handler is wired) or `attachment-pending` (when it is not). The editor surface renders the
 * outcome — the user always sees what happened to their dropped file (conventions.md §3 — no
 * silent failures).
 *
 * This extension does NOT itself call the import / upload endpoints — that would couple the
 * editor to ingestion's surface. It *classifies* the drop and invokes injected callbacks the
 * editor surface supplies (`onMarkdownImport`, `onAttachment`); the surface owns the actual
 * network calls and the toast/affordance.
 */

import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { getLogger } from '@/api';
import type { DropOutcome } from '../editorTypes';
import type { NoteId } from '@/types';

const log = getLogger('editor.drop');

/** A file is treated as markdown-to-import when its name ends in one of these (case-insensitive). */
const MARKDOWN_EXTENSIONS = ['.md', '.markdown', '.mdown', '.mkd'];

/** True iff `fileName` looks like a markdown file by extension. */
function isMarkdownFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return MARKDOWN_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * The injected drop callbacks the editor surface supplies — the seam that keeps the editor
 * from importing ingestion's / the attachment workstream's surface directly (editor-chat.md
 * §3.1).
 */
export interface DropHandlers {
  /**
   * Route a dropped `.md` file to the ingestion bulk-import path (editor-chat.md §1.2 / §3.1).
   * The surface owns the actual `sources/markdown/import` call; this extension only hands it
   * the `File`. Returns the `DropOutcome` to surface.
   */
  onMarkdownImport: (file: File) => Promise<DropOutcome>;
  /**
   * Route a dropped non-`.md` file to the attachment path for the current note (editor-chat.md
   * §1.2 / §3.1). When the REVIEW M1 attachments contract is not yet wired, the surface
   * returns `attachment-pending` rather than failing — never a silent drop. `noteId` is
   * `null` for an unsaved scratch buffer (an attachment needs a note to attach *to* — the
   * surface surfaces `rejected` with that reason).
   */
  onAttachment: (file: File, noteId: NoteId | null) => Promise<DropOutcome>;
  /** Called with every drop's resolved `DropOutcome` so the surface can render the affordance. */
  onOutcome: (outcome: DropOutcome) => void;
}

/**
 * Build the drop extension over the current note id + the injected drop handlers.
 *
 * `getNoteId` is a thunk (not a value) because the editor's note id can change without the
 * extension being rebuilt — the surface keeps the *current* note id behind it. On a drop, each
 * dropped file is classified by extension and routed: `.md` → `onMarkdownImport`, other →
 * `onAttachment`. Every file's `DropOutcome` is reported via `onOutcome`.
 *
 * Multiple files in one drop are each routed independently — a mixed drop (`.md` + a PNG) does
 * the right thing per file.
 *
 * @param getNoteId - Returns the editor's *current* note id (`null` for a scratch buffer).
 * @param handlers - The injected import / attachment / outcome callbacks.
 */
export function dropExtension(
  getNoteId: () => NoteId | null,
  handlers: DropHandlers,
): Extension {
  const handleDrop = (event: DragEvent, view: EditorView): boolean => {
    const files = event.dataTransfer?.files;
    if (files === undefined || files.length === 0) {
      // No files — this is a text/selection drag; let CM6's default drop (which inserts the
      // dragged text) run. Returning false does NOT intercept.
      return false;
    }
    // There ARE files — intercept: a file drop must never land as raw text in the buffer.
    event.preventDefault();
    const noteId = getNoteId();
    // The doc position under the drop, captured now (the async upload resolves later). Falls
    // back to the caret if the drop point can't be resolved.
    const dropPos =
      view.posAtCoords({ x: event.clientX, y: event.clientY }) ?? view.state.selection.main.head;
    log.debug('files_dropped', { count: files.length, hasNote: noteId !== null });

    // Route each file independently. The routing is async (the surface's callbacks make
    // network calls); we fire them and report each outcome as it resolves — the editor does
    // not block on the drop.
    for (const file of Array.from(files)) {
      const route = isMarkdownFile(file.name)
        ? handlers.onMarkdownImport(file)
        : handlers.onAttachment(file, noteId);
      route
        .then((outcome) => {
          // A stored attachment → insert its reference at the drop point. Images render inline
          // (`![…](attachment:<id>)`); other files become a download link. The canonical body
          // holds only the reference — the bytes live in the blob store.
          if (outcome.kind === 'attachment') {
            const ref = `attachment:${outcome.attachmentId}`;
            const md = outcome.isImage
              ? `\n![${outcome.fileName}](${ref})\n`
              : `\n[${outcome.fileName}](${ref})\n`;
            view.dispatch({
              changes: { from: dropPos, to: dropPos, insert: md },
              userEvent: 'input.drop',
            });
          }
          handlers.onOutcome(outcome);
        })
        .catch((err) => {
          // A routing callback that throws is surfaced as a `rejected` outcome — never a
          // silent drop (conventions.md §3). The surface renders it.
          log.error('drop_routing_failed', { fileName: file.name, error: String(err) });
          handlers.onOutcome({
            kind: 'rejected',
            fileName: file.name,
            reason: 'The dropped file could not be processed.',
          });
        });
    }
    return true; // handled — the file drop never becomes buffer text
  };

  return EditorView.domEventHandlers({
    drop: handleDrop,
    // Suppress the browser's default dragover (which would otherwise show a "no-drop" cursor
    // and, for some file types, navigate away from the app on drop).
    dragover: (event: DragEvent): boolean => {
      if (event.dataTransfer?.types.includes('Files')) {
        event.preventDefault();
        return true;
      }
      return false;
    },
  });
}
